import { isUrlParamEnabled } from '@theme/url-param-state';
import { fetchConfig, onDocumentReady } from '@theme/utilities';
import { CartDiscountUpdateEvent, StandardEvents } from '@shopify/events';

const PROMO_CONFIG_SELECTOR = '[data-collection-discount-active="true"][data-collection-discount-code]';
const PROMO_CODE_STORAGE_KEY_PREFIX = 'zpt:promo-discount-code:';
const PROMO_PARAM_INDEX_STORAGE_KEY = 'zpt:promo-discount-params';
const PROMO_SYNC_SOURCE_ID = 'promo-discount-sync';
let activeSyncPromise = null;

/**
 * @typedef {{
 *   cart_level_discount_applications?: Array<{ type?: string, title?: string }>,
 *   items?: Array<{
 *     line_level_discount_allocations?: Array<{
 *       discount_application?: { type?: string, title?: string }
 *     }>
 *   }>
 * }} CartData
 */

/**
 * @returns {Storage | null}
 */
function getSessionStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeValue(value) {
  return value?.trim() || '';
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeDiscountCode(value) {
  return normalizeValue(value).toUpperCase();
}

/**
 * @returns {string[]}
 */
function getTrackedPromoParams() {
  const storage = getSessionStorage();
  if (!storage) return [];

  try {
    const rawValue = storage.getItem(PROMO_PARAM_INDEX_STORAGE_KEY);
    if (!rawValue) return [];

    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue) ? parsedValue.filter((value) => typeof value === 'string' && value) : [];
  } catch {
    return [];
  }
}

/**
 * @param {string[]} params
 */
function setTrackedPromoParams(params) {
  const storage = getSessionStorage();
  if (!storage) return;

  storage.setItem(PROMO_PARAM_INDEX_STORAGE_KEY, JSON.stringify(Array.from(new Set(params))));
}

/**
 * @param {string} param
 * @returns {string}
 */
function getPromoCodeStorageKey(param) {
  return `${PROMO_CODE_STORAGE_KEY_PREFIX}${param}`;
}

/**
 * @param {string} param
 * @param {string} discountCode
 */
function persistPromoDiscountCode(param, discountCode) {
  const storage = getSessionStorage();
  if (!storage || !param || !discountCode) return;

  storage.setItem(getPromoCodeStorageKey(param), discountCode);
  setTrackedPromoParams([...getTrackedPromoParams(), param]);
}

/**
 * @param {string} param
 * @returns {string}
 */
function getPersistedPromoDiscountCode(param) {
  const storage = getSessionStorage();
  if (!storage || !param) return '';

  return normalizeDiscountCode(storage.getItem(getPromoCodeStorageKey(param)));
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isPromoDiscountDisplayed(element) {
  return element.querySelector('.collection-promo-price-note') instanceof HTMLElement;
}

/**
 * @returns {string[]}
 */
function getActivePromoDiscountCodes() {
  /** @type {Set<string>} */
  const alwaysOnPromoCodes = new Set();
  /** @type {Map<string, string>} */
  const promoCodesByParam = new Map();

  document.querySelectorAll(PROMO_CONFIG_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLElement)) return;

    if (!isPromoDiscountDisplayed(element)) return;

    const param = normalizeValue(element.dataset.collectionDiscountParam);
    const discountCode = normalizeDiscountCode(element.dataset.collectionDiscountCode);
    if (!discountCode) return;

    if (!param) {
      alwaysOnPromoCodes.add(discountCode);
      return;
    }

    persistPromoDiscountCode(param, discountCode);

    if (isUrlParamEnabled(param)) {
      promoCodesByParam.set(param, discountCode);
    }
  });

  getTrackedPromoParams().forEach((param) => {
    if (promoCodesByParam.has(param) || !isUrlParamEnabled(param)) return;

    const discountCode = getPersistedPromoDiscountCode(param);
    if (!discountCode) return;

    promoCodesByParam.set(param, discountCode);
  });

  return Array.from(new Set([...alwaysOnPromoCodes, ...Array.from(promoCodesByParam.values())]));
}

/**
 * @param {CartData} cartData
 * @returns {string[]}
 */
function getAppliedDiscountCodes(cartData) {
  const codes = [];

  (cartData.cart_level_discount_applications || []).forEach((discountApplication) => {
    if (discountApplication?.type === 'discount_code') {
      const discountCode = normalizeDiscountCode(discountApplication.title);
      if (discountCode) codes.push(discountCode);
    }
  });

  (cartData.items || []).forEach((item) => {
    (item.line_level_discount_allocations || []).forEach((discountAllocation) => {
      if (discountAllocation.discount_application?.type === 'discount_code') {
        const discountCode = normalizeDiscountCode(discountAllocation.discount_application.title);
        if (discountCode) codes.push(discountCode);
      }
    });
  });

  return Array.from(new Set(codes));
}

/**
 * @returns {Promise<CartData>}
 */
async function fetchCartData() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to load cart data: ${response.status}`);
  }

  return response.json();
}

/**
 * @returns {HTMLFormElement | null}
 */
function findCartDiscountForm() {
  const cartDiscountComponents = Array.from(document.querySelectorAll('cart-discount-component'));

  for (const component of cartDiscountComponents) {
    if (!(component instanceof HTMLElement)) continue;

    const form = component.querySelector('form.cart-discount__form');
    const input = form?.querySelector('input[name="discount"]');

    if (form instanceof HTMLFormElement && input instanceof HTMLInputElement) {
      return form;
    }
  }

  return null;
}

/**
 * @param {string} discountCode
 * @returns {Promise<boolean>}
 */
function applyDiscountWithCartForm(discountCode) {
  const form = findCartDiscountForm();
  if (!form) return Promise.resolve(false);

  const input = form.querySelector('input[name="discount"]');
  if (!(input instanceof HTMLInputElement)) return Promise.resolve(false);

  input.value = discountCode;

  return new Promise((resolve) => {
    let settled = false;

    const settle = (didApply) => {
      if (settled) return;
      settled = true;
      document.removeEventListener(StandardEvents.cartDiscountUpdate, handleDiscountUpdate);
      clearTimeout(timeoutId);
      resolve(didApply);
    };

    const handleDiscountUpdate = () => settle(true);
    const timeoutId = window.setTimeout(() => settle(false), 1500);

    document.addEventListener(StandardEvents.cartDiscountUpdate, handleDiscountUpdate, { once: true });
    form.dispatchEvent(new SubmitEvent('submit', { bubbles: true, cancelable: true }));
  });
}

/**
 * @param {CartData} cartData
 */
function refreshCartSections(cartData) {
  const deferredDiscountUpdate = CartDiscountUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartDiscountUpdateEvent({
      discountCodes: getAppliedDiscountCodes(cartData).map((code) => ({ code })),
      promise: deferredDiscountUpdate.promise,
      detail: {
        source: PROMO_SYNC_SOURCE_ID,
      },
    })
  );
  deferredDiscountUpdate.resolve({
    cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cartData),
    detail: {
      source: PROMO_SYNC_SOURCE_ID,
    },
  });
}

async function syncPromoDiscounts() {
  if (activeSyncPromise) {
    return activeSyncPromise;
  }

  activeSyncPromise = (async () => {
  const promoDiscountCodes = getActivePromoDiscountCodes();
  if (promoDiscountCodes.length === 0) return;

  const cartData = await fetchCartData();
  const existingDiscountCodes = getAppliedDiscountCodes(cartData);
  const missingDiscountCodes = promoDiscountCodes.filter((discountCode) => !existingDiscountCodes.includes(discountCode));

  if (missingDiscountCodes.length === 0) {
    refreshCartSections(cartData);
    return;
  }

  if (await applyDiscountWithCartForm(missingDiscountCodes[0])) {
    return;
  }

  const nextDiscountCodes = Array.from(new Set([...existingDiscountCodes, ...missingDiscountCodes]));
  const response = await fetch(
    Theme.routes.cart_update_url,
    fetchConfig('json', {
      body: JSON.stringify({
        discount: nextDiscountCodes.join(','),
      }),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to sync promo discount: ${response.status}`);
  }

  refreshCartSections(await response.json());
  })();

  try {
    await activeSyncPromise;
  } finally {
    activeSyncPromise = null;
  }
}

onDocumentReady(() => {
  syncPromoDiscounts().catch((error) => {
    console.error(error);
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(() => syncPromoDiscounts())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.error(error);
      });
  });
});
